import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bell, ArrowRight } from 'lucide-react';
import { resolveImageUrl } from './ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLLECTIONS = [
  { id: 'nfl-draft', label: 'NFL Draft Class', tag: 'NFL', accent: 'from-blue-600 to-blue-400' },
  { id: 'mlb-originals', label: 'MLB Originals', tag: 'MLB', accent: 'from-red-600 to-orange-400' },
  { id: 'high-school', label: 'High School Phenoms', tag: 'High School', accent: 'from-emerald-600 to-emerald-400' },
  { id: 'women-sports', label: 'Women in Sports', tag: 'Women', accent: 'from-pink-600 to-pink-400' },
  { id: 'behind-dream', label: 'Behind The Dream', tag: 'Documentary', accent: 'from-purple-600 to-purple-400' },
  { id: 'full-interviews', label: 'Full Interviews', tag: 'Interview', accent: 'from-amber-600 to-amber-400' },
];

/**
 * CollectionsStrip — clickable collection tiles. Each links to the
 * Stories index with a category prefilter via ?cat=...
 */
export const CollectionsStrip = () => {
  return (
    <section className="py-12 bg-white" data-testid="collections-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="font-italic text-xs uppercase tracking-widest text-blue-500 mb-1">Bingeable</p>
            <h2 className="font-display text-2xl sm:text-3xl uppercase">Collections</h2>
          </div>
          <Link to="/stories" className="text-sm font-bold hover-gradient hidden sm:inline">All Stories →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {COLLECTIONS.map(c => (
            <Link key={c.id} to={`/stories?cat=${encodeURIComponent(c.tag)}`}
              className={`relative aspect-[4/3] rounded-2xl overflow-hidden group bg-gradient-to-br ${c.accent}`}
              data-testid={`collection-${c.id}`}>
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition" />
              <div className="absolute inset-0 p-4 flex flex-col justify-end">
                <p className="font-display text-sm sm:text-base uppercase text-white leading-tight">{c.label}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * ComingSoonSection — pulls athlete_stories where `coming_soon: true` for the
 * current channel. Each tile shows a "Notify Me" capture that hits the
 * newsletter endpoint with a per-story tag.
 */
export const ComingSoonSection = ({ channel = 'dreams_league', accent = 'blue', dark = false } = {}) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/stories?channel=${channel}&coming_soon=true&limit=8`)
      .then(r => setItems((r.data || []).slice(0, 4)))
      .catch(() => setItems([]));
  }, [channel]);

  if (items.length === 0) return null;

  const isDark = dark;
  const sectionBg = isDark ? 'bg-black text-white' : 'bg-[#0b0b0c] text-white';
  const accentText = accent === 'gold' ? 'text-amber-300' : accent === 'white' ? 'text-white/80' : 'text-blue-300';

  return (
    <section className={`py-16 ${sectionBg}`} data-testid="coming-soon">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className={`font-italic text-xs uppercase tracking-widest mb-1 ${accentText}`}>Don&apos;t miss it</p>
            <h2 className="font-display text-3xl sm:text-4xl uppercase">Coming Soon</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map(s => <ComingSoonCard key={s.id} story={s} accent={accent} />)}
        </div>
      </div>
    </section>
  );
};

const ComingSoonCard = ({ story, accent = 'blue' }) => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email, source: 'coming_soon', tags: [`coming_soon:${story.slug}`] });
      setDone(true);
    } catch { setDone(true); }
    finally { setBusy(false); }
  };
  const accentBg = accent === 'gold' ? 'bg-amber-300 text-black' : accent === 'white' ? 'bg-white text-black' : 'bg-blue-500 text-white';
  const accentRing = accent === 'gold' ? 'focus:border-amber-300' : accent === 'white' ? 'focus:border-white' : 'focus:border-blue-400';
  const accentChipBg = accent === 'gold' ? 'bg-amber-300 text-black' : accent === 'white' ? 'bg-white text-black' : 'bg-blue-500 text-white';
  const accentTextLabel = accent === 'gold' ? 'text-amber-300' : accent === 'white' ? 'text-white/80' : 'text-blue-300';
  return (
    <div className="bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden" data-testid={`coming-soon-${story.slug}`}>
      <div className="aspect-video bg-black relative">
        {story.thumbnail && <img loading="lazy" decoding="async" src={resolveImageUrl(story.thumbnail, 400)} alt="" className="w-full h-full object-cover opacity-80" />}
        <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full ${accentChipBg} text-[10px] font-bold uppercase tracking-widest`}>Coming Soon</span>
      </div>
      <div className="p-4">
        <p className="font-display text-base uppercase line-clamp-2 mb-1">{story.name}</p>
        {story.release_date && <p className={`text-[11px] uppercase tracking-widest mb-3 ${accentTextLabel}`}>{story.release_date}</p>}
        {done ? (
          <p className="text-xs text-green-300 flex items-center gap-1.5"><Bell className="w-3.5 h-3.5" /> We&apos;ll ping you</p>
        ) : (
          <form onSubmit={submit} className="flex gap-2">
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" className={`flex-1 px-3 py-2 rounded-full bg-white/5 border border-white/15 text-xs text-white placeholder-white/40 focus:outline-none ${accentRing}`} data-testid={`coming-soon-email-${story.slug}`} />
            <button type="submit" disabled={busy} className={`px-3 py-2 rounded-full ${accentBg} text-xs font-bold disabled:opacity-60 inline-flex items-center gap-1`} data-testid={`coming-soon-notify-${story.slug}`}>
              Notify <ArrowRight className="w-3 h-3" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
